import {
  DEFAULT_POSITION_ALERTS,
  type RollAlertCriteria,
} from "@/lib/position-alert-defaults";

export const CUSTOM_WHEEL_STRATEGY = "CUSTOM" as const;
export const STEADY_INCOME_STRATEGY = "STEADY_INCOME" as const;
export const WHEELER_STRATEGY = "WHEELER" as const;
export const PREMIUM_HUNTER_STRATEGY = "PREMIUM_HUNTER" as const;

export const WHEEL_STRATEGY_KEYS = [
  CUSTOM_WHEEL_STRATEGY,
  STEADY_INCOME_STRATEGY,
  WHEELER_STRATEGY,
  PREMIUM_HUNTER_STRATEGY,
] as const;

export type WheelStrategyKey = (typeof WHEEL_STRATEGY_KEYS)[number];
export type NonCustomWheelStrategyKey = Exclude<WheelStrategyKey, typeof CUSTOM_WHEEL_STRATEGY>;

export type SuggestedActionType =
  | "split"
  | "itm_warning"
  | "expiration"
  | "early_close"
  | "roll"
  | "covered_call"
  | "top_csp"
  | "idle_cash"
  | "recent_alert";

export interface StrategyEntryFilters {
  strategies: string[];
  daysFilter: [number, number];
  yieldFilter: [number, number];
  diffFilter: [number, number];
  waprFilter: [number, number];
  riskOfAssignmentFilter: [number, number];
  ratingFilter: [number, number];
  strikeFilter?: [number, number];
  ivFilter: [number, number];
  openInterestFilter: [number, number];
  includesEarnings: "any" | "yes" | "no";
  marketCapFilter?: [number, number];
  fScoreFilter?: [number, number];
  peRatioFilter?: [number, number];
  pegFilter?: [number, number];
  sentimentFilter?: [number, number];
  rsiTechnicalFilter?: [number, number];
  momentumFilter?: [number, number];
}

export interface StrategyPositionAlerts {
  expirationDte: number;
  earlyCloseEfficiencyRatio: number;
  splitEnabled: boolean;
  coveredCallMinRoiAtBe: number;
}

export interface StrategyModalCopy {
  summary: string;
  entryAlertStrategy: string;
  rollSuggestions: string;
  coveredCallTargeting: string;
  riskAlerts: string;
  workflowPriorities: string;
}

export interface WheelStrategyConfig {
  key: WheelStrategyKey;
  systemKey: string;
  label: string;
  shortLabel: string;
  description: string;
  signatureClassName: string;
  /** Subtle per-strategy accent (hex) used for the picker and strategy callouts across the app. */
  accent: string;
  entryFilters: StrategyEntryFilters | null;
  defaultAlertMinRating: number;
  rollCriteria: RollAlertCriteria;
  positionAlerts: StrategyPositionAlerts;
  actionPriority: Record<SuggestedActionType, number>;
  modalCopy: StrategyModalCopy;
}

export interface ResolvedWheelStrategyConfig extends WheelStrategyConfig {
  entryFilters: StrategyEntryFilters | null;
}

const DEFAULT_PRIORITY: Record<SuggestedActionType, number> = {
  split: 100,
  itm_warning: 95,
  expiration: 90,
  roll: 92,
  early_close: 80,
  covered_call: 60,
  top_csp: 50,
  idle_cash: 40,
  recent_alert: 30,
};

const CUSTOM_CONFIG: WheelStrategyConfig = {
  key: CUSTOM_WHEEL_STRATEGY,
  systemKey: "custom",
  label: "Custom",
  shortLabel: "Custom",
  description: "Use your saved searches, manual filters, and custom suggestion settings.",
  signatureClassName: "from-zinc-300/30 via-zinc-200/20 to-zinc-400/20",
  accent: "#7dd3fc",
  entryFilters: null,
  defaultAlertMinRating: 80,
  rollCriteria: DEFAULT_POSITION_ALERTS.roll[0].criteria,
  positionAlerts: {
    expirationDte: DEFAULT_POSITION_ALERTS.expiration.dte,
    earlyCloseEfficiencyRatio: DEFAULT_POSITION_ALERTS.early_close.efficiencyRatio,
    splitEnabled: DEFAULT_POSITION_ALERTS.split.enabled,
    coveredCallMinRoiAtBe: DEFAULT_POSITION_ALERTS.covered_call.minRoiAtBe,
  },
  actionPriority: DEFAULT_PRIORITY,
  modalCopy: {
    summary: "Nothing is overridden. Your own filters, saved searches, alerts, and suggestion settings drive the Wheel.",
    entryAlertStrategy: "Uses your saved-search alerts and manual filters.",
    rollSuggestions: "Uses your custom roll alert settings, or product defaults if you never changed them.",
    coveredCallTargeting: "Uses your covered-call suggestion setting.",
    riskAlerts: "Uses your expiration, early-close, split, and roll alert settings.",
    workflowPriorities: "Keeps the current QuantWheel ordering.",
  },
};

const STEADY_INCOME_CONFIG: WheelStrategyConfig = {
  key: STEADY_INCOME_STRATEGY,
  systemKey: "steady-income",
  label: "Steady Income",
  shortLabel: "Steady",
  description: "Conservative wheel setup focused on quality, liquidity, and calmer income.",
  signatureClassName: "from-zinc-300/35 via-emerald-200/15 to-zinc-500/20",
  accent: "#34d399",
  entryFilters: {
    strategies: ["Cash Secured Put"],
    daysFilter: [0, 45],
    yieldFilter: [0, 100],
    diffFilter: [5, 1000],
    waprFilter: [12, 1000],
    riskOfAssignmentFilter: [0, 0.15],
    ratingFilter: [50, 10000],
    ivFilter: [0, 500],
    openInterestFilter: [50, 1_000_000],
    includesEarnings: "no",
    marketCapFilter: [10_000_000_000, 10_000_000_000_000],
    fScoreFilter: [6, 9],
    pegFilter: [0, 4],
    sentimentFilter: [0, 1],
    momentumFilter: [0, 1],
  },
  defaultAlertMinRating: 80,
  rollCriteria: {
    diffPercent: { max: 5 },
    yearlyYield: { min: 12 },
    daysExtension: { max: 350 },
    delta: { max: 0.35 },
  },
  positionAlerts: {
    expirationDte: 7,
    earlyCloseEfficiencyRatio: 1.35,
    splitEnabled: true,
    coveredCallMinRoiAtBe: 10,
  },
  actionPriority: {
    split: 100,
    itm_warning: 96,
    expiration: 94,
    roll: 90,
    early_close: 88,
    covered_call: 68,
    top_csp: 55,
    idle_cash: 45,
    recent_alert: 35,
  },
  modalCopy: {
    summary: "Quality-first presets for users who want lower-delta, liquid opportunities and earlier risk warnings.",
    entryAlertStrategy: "Alerts on 0-45 DTE CSPs under 15 delta, rating 50+, OI 50+, no earnings, $10B+ cap, and F-Score 6-9.",
    rollSuggestions: "Rolls favor long extensions when needed, 12%+ yearly yield, and assignment delta under 35.",
    coveredCallTargeting: "Covered-call ideas start at 10% ROI at breakeven and prefer safer OTM income.",
    riskAlerts: "Expiration warnings begin 7 days out, with early-close nudges at 1.35x efficiency.",
    workflowPriorities: "Risk review first, then expirations, early closes, safe rolls, covered calls, and new CSPs.",
  },
};

const WHEELER_CONFIG: WheelStrategyConfig = {
  key: WHEELER_STRATEGY,
  systemKey: "wheeler",
  label: "Wheeler",
  shortLabel: "Wheeler",
  description: "Wheel workflow for actively selling puts, managing rolls, and selling covered calls.",
  signatureClassName: "from-zinc-300/30 via-lime-200/15 to-zinc-500/20",
  accent: "#a3e635",
  entryFilters: {
    strategies: ["Cash Secured Put"],
    daysFilter: [0, 45],
    yieldFilter: [0, 100],
    diffFilter: [2, 1000],
    waprFilter: [20, 1000],
    riskOfAssignmentFilter: [0.0, 0.30],
    ratingFilter: [50, 10000],
    ivFilter: [0, 500],
    openInterestFilter: [10, 1_000_000],
    includesEarnings: "any",
    marketCapFilter: [2_000_000_000, 10_000_000_000_000],
    fScoreFilter: [6, 9],
  },
  defaultAlertMinRating: 80,
  rollCriteria: {
    diffPercent: { max: 2 },
    yearlyYield: { min: 15 },
    daysExtension: { max: 180 },
  },
  positionAlerts: {
    expirationDte: 3,
    earlyCloseEfficiencyRatio: 2,
    splitEnabled: true,
    coveredCallMinRoiAtBe: 20,
  },
  actionPriority: {
    split: 100,
    itm_warning: 96,
    expiration: 92,
    roll: 90,
    top_csp: 78,
    covered_call: 74,
    early_close: 66,
    idle_cash: 60,
    recent_alert: 35,
  },
  modalCopy: {
    summary: "Wheel presets for users who don't mind being assigned and get their shares called away during CC phase.",
    entryAlertStrategy: "Alerts on 0-45 DTE CSPs up to 30 delta, rating 50+, 20%+ yearly yield, OI 10+, $2B+ cap, earnings allowed.",
    rollSuggestions: "Rolls favor 15%+ yearly yield and extensions under 180 days when positions approach the strike.",
    coveredCallTargeting: "Covered-call ideas start at 20% ROI at breakeven.",
    riskAlerts: "Expiration warnings begin 3 days out, with early-close nudges at 2x efficiency.",
    workflowPriorities: "Expirations and rolls first, then new CSPs, covered calls, and early-close opportunities.",
  },
};

const PREMIUM_HUNTER_CONFIG: WheelStrategyConfig = {
  key: PREMIUM_HUNTER_STRATEGY,
  systemKey: "premium-hunter",
  label: "Premium Hunter",
  shortLabel: "Premium",
  description: "Aggressive setup for short-dated, high-premium opportunities.",
  signatureClassName: "from-zinc-300/30 via-amber-200/15 to-zinc-500/20",
  accent: "#fbbf24",
  entryFilters: {
    strategies: ["Cash Secured Put"],
    daysFilter: [0, 14],
    yieldFilter: [0, 100],
    diffFilter: [-100, 1000],
    waprFilter: [40, 1000],
    riskOfAssignmentFilter: [0.0, 0.40],
    ratingFilter: [85, 10000],
    ivFilter: [0, 500],
    openInterestFilter: [10, 1_000_000],
    includesEarnings: "any",
  },
  defaultAlertMinRating: 80,
  rollCriteria: {
    diffPercent: { max: 1 },
    yearlyYield: { min: 20 },
    daysExtension: { max: 80 },
  },
  positionAlerts: {
    expirationDte: 1,
    earlyCloseEfficiencyRatio:2.5,
    splitEnabled: true,
    coveredCallMinRoiAtBe: 30,
  },
  actionPriority: {
    split: 100,
    itm_warning: 96,
    top_csp: 92,
    roll: 88,
    covered_call: 82,
    expiration: 76,
    early_close: 66,
    idle_cash: 64,
    recent_alert: 35,
  },
  modalCopy: {
    summary: "Aggressive presets for users who want short-dated, high-rating, high-yield premium opportunities.",
    entryAlertStrategy: "Alerts on 0-14 DTE CSPs up to 40 delta, rating 85+, 40%+ yearly yield, OI 10+, earnings allowed.",
    rollSuggestions: "Rolls favor 20%+ yearly yield and extensions under 80 days when positions are near the strike.",
    coveredCallTargeting: "Covered-call ideas start at 30% ROI at breakeven.",
    riskAlerts: "Expiration warnings begin 1 day out, with early-close nudges at 2.5x efficiency.",
    workflowPriorities: "High-premium CSPs and high-yield rolls move up, while risk warnings remain visible.",
  },
};

export const WHEEL_STRATEGY_CONFIGS: Record<WheelStrategyKey, WheelStrategyConfig> = {
  [CUSTOM_WHEEL_STRATEGY]: CUSTOM_CONFIG,
  [STEADY_INCOME_STRATEGY]: STEADY_INCOME_CONFIG,
  [WHEELER_STRATEGY]: WHEELER_CONFIG,
  [PREMIUM_HUNTER_STRATEGY]: PREMIUM_HUNTER_CONFIG,
};

export function isWheelStrategyKey(value: unknown): value is WheelStrategyKey {
  return typeof value === "string" && WHEEL_STRATEGY_KEYS.includes(value as WheelStrategyKey);
}

export function normalizeWheelStrategyKey(value: unknown): WheelStrategyKey {
  if (isWheelStrategyKey(value)) return value;
  return CUSTOM_WHEEL_STRATEGY;
}

export function isCustomWheelStrategy(value: unknown): boolean {
  return normalizeWheelStrategyKey(value) === CUSTOM_WHEEL_STRATEGY;
}

export function getWheelStrategyConfig(strategy: unknown): WheelStrategyConfig {
  return WHEEL_STRATEGY_CONFIGS[normalizeWheelStrategyKey(strategy)];
}

export function getWheelStrategyAccent(strategy: unknown): string {
  return getWheelStrategyConfig(strategy).accent;
}

export function getNonCustomWheelStrategies(): WheelStrategyConfig[] {
  return [
    STEADY_INCOME_CONFIG,
    WHEELER_CONFIG,
    PREMIUM_HUNTER_CONFIG,
  ];
}

export function getWheelStrategyOptions(): WheelStrategyConfig[] {
  return WHEEL_STRATEGY_KEYS.map((key) => WHEEL_STRATEGY_CONFIGS[key]);
}

export function resolveStrategyEntryFilters(
  strategy: unknown,
  maxCollateralPerTrade?: number | null
): StrategyEntryFilters | null {
  const config = getWheelStrategyConfig(strategy);
  if (!config.entryFilters) return null;

  const filters: StrategyEntryFilters = {
    ...config.entryFilters,
    strategies: [...config.entryFilters.strategies],
    daysFilter: [...config.entryFilters.daysFilter] as [number, number],
    yieldFilter: [...config.entryFilters.yieldFilter] as [number, number],
    diffFilter: [...config.entryFilters.diffFilter] as [number, number],
    waprFilter: [...config.entryFilters.waprFilter] as [number, number],
    riskOfAssignmentFilter: [...config.entryFilters.riskOfAssignmentFilter] as [number, number],
    ratingFilter: [...config.entryFilters.ratingFilter] as [number, number],
    ivFilter: [...config.entryFilters.ivFilter] as [number, number],
    openInterestFilter: [...config.entryFilters.openInterestFilter] as [number, number],
  };

  if (typeof maxCollateralPerTrade === "number" && maxCollateralPerTrade > 0 && maxCollateralPerTrade < 50_000) {
    filters.strikeFilter = [0, maxCollateralPerTrade / 100];
  }

  return filters;
}

export function resolveWheelStrategyConfig(
  strategy: unknown,
  maxCollateralPerTrade?: number | null
): ResolvedWheelStrategyConfig {
  const config = getWheelStrategyConfig(strategy);
  return {
    ...config,
    entryFilters: resolveStrategyEntryFilters(strategy, maxCollateralPerTrade),
  };
}

export function getStrategyActionPriority(
  strategy: unknown,
  actionType: SuggestedActionType
): number {
  const config = getWheelStrategyConfig(strategy);
  return config.actionPriority[actionType] ?? DEFAULT_PRIORITY[actionType] ?? 0;
}

export function getStrategySystemSearchKey(strategy: unknown): string | null {
  const key = normalizeWheelStrategyKey(strategy);
  if (key === CUSTOM_WHEEL_STRATEGY) return null;
  return WHEEL_STRATEGY_CONFIGS[key].systemKey;
}

export interface StrategyColumnFilterDefaults {
  daysExtension?: { max?: number };
  yearlyYield?: { min?: number };
  riskOfAssignment?: { max?: number };
}

export function rollCriteriaToColumnFilters(
  criteria: RollAlertCriteria | undefined
): StrategyColumnFilterDefaults {
  const filters: StrategyColumnFilterDefaults = {};
  if (!criteria) return filters;
  if (criteria.daysExtension?.max != null) {
    filters.daysExtension = { max: criteria.daysExtension.max };
  }
  if (criteria.yearlyYield?.min != null) {
    filters.yearlyYield = { min: criteria.yearlyYield.min };
  }
  if (criteria.delta?.max != null) {
    filters.riskOfAssignment = { max: criteria.delta.max * 100 };
  }
  return filters;
}
